import React, { useState } from "react";
import { Typography, Chip, Avatar } from "@mui/material";
import CustomTable from "../../components/Table/Table";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { useAllSkillsViewModel } from "./AllSkillViewModel.js";
// import AddClient from "../AddClient/AddClient";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import SkillCategoryIcon from "@mui/icons-material/Category"
import { useLoading } from "../../contexts/LoadingContext";
import Modal from "../../components/Modal/Modal";
import ConfirmationModal from "../../components/ConfirmationModal/ConfirmationModal";
import AddSkill from "./AddSkill.js";
import { useRbac } from "../../hooks/RbacContext.js";

const SkillLanding = () => {
  const {
    skills,
    loading,
    error,
    handleDelete,
    createSkill,
    updateSkill,
    refetch,
    handleSearch,
    skillCategory,
    getSkillCategoryName
  } = useAllSkillsViewModel();

  const { showLoading, hideLoading } = useLoading();

  const [isAddSkillOpen, setIsAddSkillOpen] = useState(false);
  const [selectedSkill, setSelectedSkill] = useState(null);
  const [successModal, setSuccessModal] = useState(null);
  const [deleteConfirmation, setDeleteConfirmation] = useState(null);
  const {checkAccess} = useRbac();

  const columns = [
    {
      id: "skill_name",
      label: "Name",
    },
    {
      id: "skill_cat_id",
      label: "Skill Category",
      render: (value) => (
        <Typography
          variant="body2"
          noWrap
          sx={{
            fontSize: "inherit",
            color: (theme) =>
              value ? theme.palette.text.primary : theme.palette.text.secondary,
          }}
        >
          {getSkillCategoryName(value)}
        </Typography>
      ),
      searchValue: (value) => getSkillCategoryName(value),
    },
    {
      id: "skill_description",
      label: "Description",
      align: "left",
    },
    {
      id: "created_on",
      label: "Created On",
      align: "left",
      render: (value) => {
        if (!value) return '-';
        const [day, month, year] = value.split('/');
        const date = new Date(year, month - 1, day);
        return date.toLocaleDateString("en-GB", {
          day: "2-digit",
          month: "short",
          year: "numeric"
        });
      }
    },
    {
      id: "updated_on",
      label: "Updated On",
      align: "left",
      render: (value) => {
        if (!value) return '-';
        const [day, month, year] = value.split('/');
        const date = new Date(year, month - 1, day);
        return date.toLocaleDateString("en-GB", {
          day: "2-digit",
          month: "short",
          year: "numeric"
        });
      }
    },
  ];
  const fixedColumns = {
    left: [],
    right: ["actions"],
  };

  
  const handleAddClient = () => {
    setSelectedSkill(null);
    setIsAddSkillOpen(true);
  };

  const handleEditClient = (client) => {
    setSelectedSkill(client);
    setIsAddSkillOpen(true);
  };

  const handleCloseDialog = () => {
    setIsAddSkillOpen(false);
    setSelectedSkill(null);
  };

  const handleSubmit = async (formData) => {
    try {
      showLoading();
      if (selectedSkill) {
        await updateSkill(selectedSkill.skill_id, formData);
        setSuccessModal({
          type: "success",
          title: "Success",
          message: "Skill updated successfully",
        });
      } else {
        await createSkill(formData);
        setSuccessModal({
          type: "success",
          title: "Success",
          message: "Skill added successfully",
        });
      }
      handleCloseDialog();
      await refetch();
    } catch (error) {
      throw error;
    } finally {
      hideLoading();
    }
  };

  const handleCloseSuccessModal = () => {
    setSuccessModal(null);
  };

  const handleDeleteClick = (skill) => {
    setDeleteConfirmation({
      skill,
      open: true,
    });
  };

  const handleDeleteConfirm = async () => {
    const skill = deleteConfirmation.skill;
    try {
      showLoading();
      await handleDelete(skill);
      setDeleteConfirmation(null);
      setSuccessModal({
        type: "success",
        title: "Success",
        message: "Skill deleted successfully",
      });
    } catch (error) {
      setSuccessModal({
        type: "error",
        title: "Action Failed",
        message: error.message || "Failed to delete Skill",
      });
    } finally {
      hideLoading();
    }
  };

  const getActions = (client) => {
    const actions = [];
    if (checkAccess("skill", "update")) {
      actions.push({
        icon: <EditIcon fontSize="small" />,
        label: "Edit",
        onClick: handleEditClient,
        color: "primary",
        sx: {
          "&:hover": {
            backgroundColor: (theme) =>
              theme.palette.primary.lighter || "rgba(33, 150, 243, 0.08)",
          },
        },
      });
    }
    if (checkAccess("skill", "delete")) {
      actions.push({
        icon: <DeleteIcon fontSize="small" />,
        label: "Delete",
        onClick: handleDeleteClick,
        color: "error",
        sx: {
          "&:hover": {
            backgroundColor: (theme) =>
              theme.palette.error.lighter || "rgba(211, 47, 47, 0.08)",
          },
        },
      });
    }

    return actions;
  };

  if (error) {
    return (
      <div>
        <Typography color="error">Error: {error}</Typography>
      </div>
    );
  }
  const columnFilters = [
    {
      id: "skill_cat_id",
      label: "Skill Category",
      render: (value) => getSkillCategoryName(value),
      searchValue: (value) => getSkillCategoryName(value),
      icon: <SkillCategoryIcon />
    }
  ];
  return (
    <div>
      <CustomTable
        columns={columns}
        data={skills}
        onAdd={handleAddClient}
        showActions={true}
        showAddButton={checkAccess("skill","add")}
        addButtonIcon={<PersonAddIcon />}
        showSearch={true}
        showColumnVisibility={false}
        pageSize={10}
        stickyHeader={true}
        fixedColumns={fixedColumns}
        actions={getActions}
        title="All Skills"
        subtitle="Manage and monitor all skills"
        addButtonText="Add Skill"
        alignment="left"
        fontSize="12px"
        showFilters={true}
        columnFilters={columnFilters}
      />

      <AddSkill
        open={isAddSkillOpen}
        onClose={handleCloseDialog}
        initialData={selectedSkill}
        onSubmit={handleSubmit}
        skillCategory={skillCategory}
        getSkillCategoryName={getSkillCategoryName}
      />

      {successModal && (
        <Modal
          type={successModal.type}
          title={successModal.title}
          message={successModal.message}
          onClose={handleCloseSuccessModal}
        />
      )}

      {deleteConfirmation && (
        <ConfirmationModal
          open={true}
          title="Delete Skill"
          message={`Are you sure you want to delete skill "${deleteConfirmation.skill.skill_name}"?`}
          onClose={() => setDeleteConfirmation(null)}
          onConfirm={handleDeleteConfirm}
        />
      )}
    </div>
  );
};

export default SkillLanding;
